// The forecast (ERP-PLAN §5.4, "Forecasting (decided 2026-10-28)"): one per sell item, used by the
// Purchasing plan, shown on the Forecast page and given to Claude, so they always agree. Two halves:
//
// 1. computeBase(), nightly on the server (server/src/forecast.ts) from daily sales and stock history,
//    stored per item in forecast_base (synced):
//    - Base rate over *in-stock* days: a day that ended out of stock at HHL (and sold nothing) doesn't
//      count, so a sold-out month doesn't look like weak demand: 150 sold in 31 days with 15 out = 9.4/day.
//      A window with under 60 in-stock days gets that share of the adjustment (toward units ÷ all days),
//      since a few days after a restock oversell. 60% the last 90 days (reaching back until it covers 30
//      in-stock days; less weight when it has under 60), 40% the last 12 months brought to now by the trend.
//      An item first sold under two years ago uses the recent rate alone: its 12 months still hold the launch.
//      Before Shopify's stock history starts (about August 2023) raw sales are used, and flagged.
//    - Seasonal factor per month: the item's own when it has 2+ years with enough sales and the pattern
//      repeats; else its product type's, else its project's, else the whole store's. Growth or decline is
//      taken out first (a log-linear trend), so a growing item doesn't read as busier late in the year.
//      Factors are pulled toward 1 by how little data there is. Rates are per day at an average time of year, so a busy
//      December in the last 90 days doesn't inflate January.
//    - Trend: the last 12 months ÷ the 12 before (in-stock days, seasonality out), capped 0.5×–2×, only
//      when both years sold. The 12-month rate is centred about six months back, so it gets the square root
//      (half a year's growth), and only when the recent rate has moved the same way, no further than it: growth
//      that has levelled off isn't projected on. Chosen by backtest (`node server/src/cli.ts forecast-eval`).
//    - Accuracy: a backtest (what this method would have said at the start of each of the last 12 months)
//      until saved snapshots cover 3 months.
// 2. buildForecasts(), in the browser (and on the server for the MCP and the plan), on top of the base:
//    "forecast like SKU X at N%", scoped adjustments (all / vendor / project / product type / tag / item,
//    for a range of months; several multiply), then per-month overrides, which replace the month. B2B
//    demand stays in the plan, on its due date. Everything keeps its "why" (explainForecast()).
//
// Make items are forecast too; component demand from BOMs comes later (the make dashboard) and adds to
// a part's own forecast through dayRate().
import type {
  ForecastAdjustment,
  ForecastBase,
  ForecastOverride,
  ForecastRun,
  Item,
  ItemPlan,
  Project,
  SalesSummary,
  ShopifyProduct,
  Vendor,
  VendorItem,
} from "./db.gen.ts";
import { dayNum, fmt, isoOfDay, monthLabel, salesRates, shortDate } from "./planning.ts";

export const FORECAST = {
  recentDays: 90,
  /** The recent window reaches back until it has this many in-stock days (up to a year). */
  minInStock: 30,
  // How the base rate is put together (BaseMethod explains each). Chosen 2026-10-28 by backtest on production's
  // history (`node server/src/cli.ts forecast-eval`, 12 and 24 months): before, half the recent rate and half
  // the 12-month rate × the whole trend, which put growing items at up to 1.5–2× their recent sales.
  recentWeight: 0.6,
  recentWeightYoung: 1,
  trendPower: 0.5,
  trendConfirm: "bound" as BaseMethod["trendConfirm"],
  upliftDays: 60,
  confidenceDays: 60,
  trendMin: 0.5,
  trendMax: 2,
  /** Each year needs this many units and in-stock days for a trend. */
  trendMinUnits: 20,
  trendMinInStock: 60,
  /** Out of stock this many days in a row (with no sales): treated as stopped, forecast 0. */
  stoppedDays: 180,
  /** A month counts toward seasonality with at least this many in-stock days. */
  seasonMinDays: 7,
  /** Shrinkage: a pattern keeps n / (n + K) of itself, n = units per calendar month over the years used. */
  seasonShrink: 30,
  /** Years must agree this well (correlation) for a pattern to count. */
  seasonMinCorr: 0.3,
  /** Units a year an item (or group) needs for its own pattern. */
  itemSeasonUnits: 60,
  groupSeasonUnits: 100,
  groupMinItems: 3,
  /** Months of actual sales kept for the chart (plus the current one). */
  historyMonths: 24,
  /** Months the backtest checks. */
  backtestMonths: 12,
  /** Snapshot months needed before they replace the backtest. */
  snapshotMonths: 3,
  /** Stock history this many days (or fewer) behind sales: the forecast stops where it does. More: the days after count as in stock. */
  staleCapDays: 30,
} as const;

// Calendar ---------------------------------------------------------------------------------------------
// Days are whole numbers (days since 1970-01-01), months too (year × 12 + month − 1), so nothing meets
// time zones. A lookup table gives each day's month fast enough for every item and backtest each night.

/** 2026-11-01 → 24322. */
export const monthNum = (iso: string) => Number(iso.slice(0, 4)) * 12 + Number(iso.slice(5, 7)) - 1;
/** 24322 → 2026-11-01. */
export const monthIso = (m: number) => `${Math.floor(m / 12)}-${String((m % 12) + 1).padStart(2, "0")}-01`;
export const monthDays = (m: number) => new Date(Date.UTC(Math.floor(m / 12), (m % 12) + 1, 0)).getUTCDate();
/** The month's first day as a day number. */
export const monthFirstDay = (m: number) => Math.round(Date.UTC(Math.floor(m / 12), m % 12, 1) / 86_400_000);

const TABLE_FROM = dayNum("2000-01-01");
let monthTable: Int32Array | null = null;
/** The month a day is in. */
export function monthOfDay(d: number): number {
  if (!monthTable) {
    const to = dayNum("2060-01-01");
    monthTable = new Int32Array(to - TABLE_FROM);
    for (let m = 2000 * 12; monthFirstDay(m) < to; m++) {
      const a = monthFirstDay(m) - TABLE_FROM;
      monthTable.fill(m, a, Math.min(monthTable.length, a + monthDays(m)));
    }
  }
  const i = d - TABLE_FROM;
  if (i >= 0 && i < monthTable.length) return monthTable[i];
  const date = new Date(d * 86_400_000);
  return date.getUTCFullYear() * 12 + date.getUTCMonth();
}

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "Nov–Dec 2026", "from Mar 2027", "until Jan 2027", "every month". */
export function monthRange(from: string | null, to: string | null): string {
  const label = (iso: string) => monthLabel(iso);
  if (from && to) {
    if (from === to) return label(from);
    return from.slice(0, 4) === to.slice(0, 4) ? `${MONTH_SHORT[Number(from.slice(5, 7)) - 1]}–${label(to)}` : `${label(from)} – ${label(to)}`;
  }
  if (from) return `from ${label(from)}`;
  if (to) return `until ${label(to)}`;
  return "every month";
}

const pctText = (p: number) => `${p > 0 ? "+" : p < 0 ? "−" : ""}${fmt(Math.abs(Math.round(p * 10) / 10))}%`;
/** 1.18 → "+18%". */
export const factorText = (f: number) => pctText((f - 1) * 100);

// 1. The nightly base ----------------------------------------------------------------------------------

/** One item's daily history: index i is day `start + i`, through the last whole day. */
export type DailySeries = {
  start: number;
  /** Net units sold (returns count on their day, so a day can be negative). */
  units: ArrayLike<number>;
  /** 1 where the day ended out of stock at HHL. */
  out: ArrayLike<number>;
  /** Day of the first sale; null = never sold. */
  firstSold: number | null;
};

/**
 * The days stock history covers: from a day (open-ended), or from–to (the last pull). Days outside count as in
 * stock, flagged raw: before Shopify's history starts, or after a stock-history pull that stopped. null = none.
 */
export type StockFrom = number | { from: number; to: number } | null;
const known = (h: StockFrom, d: number) => h !== null && (typeof h === "number" ? d >= h : d >= h.from && d <= h.to);

/** A day the item could sell: it ended in stock, or it sold (it sold out that day), or there's no stock history. */
function inStock(s: DailySeries, stockFrom: StockFrom, d: number): 0 | 1 | 2 {
  if (!known(stockFrom, d)) return 2; // raw
  const i = d - s.start;
  return !s.out[i] || s.units[i] > 0 ? 1 : 0;
}

const unitsOn = (s: DailySeries, d: number) => {
  const i = d - s.start;
  return i >= 0 && i < s.units.length ? s.units[i] : 0;
};

export type MonthStats = { units: number[]; inStock: number[] };

/** Units and in-stock days per month, months `from`–`to` inclusive, counting days up to `asOf` (and from the first sale). */
export function monthStats(s: DailySeries, stockFrom: StockFrom, from: number, to: number, asOf: number): MonthStats {
  const n = to - from + 1;
  const units = new Array<number>(n).fill(0);
  const days = new Array<number>(n).fill(0);
  const first = Math.max(s.start, s.firstSold ?? Number.POSITIVE_INFINITY, monthFirstDay(from));
  const last = Math.min(asOf, monthFirstDay(to + 1) - 1, s.start + s.units.length - 1);
  for (let d = first; d <= last; d++) {
    const k = monthOfDay(d) - from;
    units[k] += unitsOn(s, d);
    if (inStock(s, stockFrom, d)) days[k] += 1;
  }
  return { units, inStock: days };
}

export type SeasonProfile = {
  /** 12 factors, Jan–Dec, averaging 1, after shrinkage. */
  factors: number[];
  /** Before shrinkage. */
  raw: number[];
  /** 12-month blocks with enough data (up to 3). */
  years: number;
  /** How alike the years are (Pearson, averaged); null with under 2 years. */
  corr: number | null;
  /** Share of the raw pattern kept. */
  weight: number;
  /** Units a year, averaged over the years used. */
  unitsPerYear: number;
  ok: boolean;
};

const FLAT = Object.freeze(Array<number>(12).fill(1)) as number[];

function pearson(a: number[], b: number[]): number | null {
  const pairs = a.map((x, i) => [x, b[i]]).filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y));
  if (pairs.length < 6) return null;
  const mx = pairs.reduce((n, [x]) => n + x, 0) / pairs.length;
  const my = pairs.reduce((n, [, y]) => n + y, 0) / pairs.length;
  let sxy = 0;
  let sxx = 0;
  let syy = 0;
  for (const [x, y] of pairs) {
    sxy += (x - mx) * (y - my);
    sxx += (x - mx) ** 2;
    syy += (y - my) ** 2;
  }
  return sxx && syy ? sxy / Math.sqrt(sxx * syy) : null;
}

/**
 * The monthly pattern from up to 3 twelve-month blocks ending at `lastMonth` (the last whole month):
 * each month's in-stock rate ÷ the trend (a log-linear fit over the blocks used, so growth or decline
 * isn't read as a pattern), ÷ its block's average, averaged over the blocks, then pulled toward 1.
 * `stats` covers months lastMonth − 35 to lastMonth.
 */
export function seasonProfile(stats: MonthStats, lastMonth: number, minUnits: number): SeasonProfile {
  const n = stats.units.length;
  const rateAt = (k: number) => (stats.inStock[k] < FORECAST.seasonMinDays ? Number.NaN : stats.units[k] / stats.inStock[k]);
  // The blocks with enough data, newest first (an older block only counts after the newer ones).
  const blocks: number[] = [];
  let units = 0;
  for (let b = 0; b < 3; b++) {
    const end = n - 1 - 12 * b;
    if (end - 11 < 0) break;
    let total = 0;
    let present = 0;
    let sum = 0;
    for (let k = end - 11; k <= end; k++) {
      total += stats.units[k];
      const rate = rateAt(k);
      if (Number.isNaN(rate)) continue;
      present++;
      sum += rate;
    }
    if (present < 10 || sum <= 0 || total < minUnits) break;
    blocks.push(end);
    units += total;
  }
  // Trend: ln(rate) = a + b·k over the months that sold.
  let sx = 0;
  let sy = 0;
  let sxx = 0;
  let sxy = 0;
  let m = 0;
  for (const end of blocks) {
    for (let k = end - 11; k <= end; k++) {
      const rate = rateAt(k);
      if (!(rate > 0)) continue;
      const y = Math.log(rate);
      sx += k;
      sy += y;
      sxx += k * k;
      sxy += k * y;
      m++;
    }
  }
  const slope = m >= 6 && m * sxx - sx * sx > 0 ? (m * sxy - sx * sy) / (m * sxx - sx * sx) : 0;
  const trendAt = (k: number) => Math.exp(slope * k);
  const profiles = blocks.map((end, b) => {
    const p = Array<number>(12).fill(Number.NaN);
    let sum = 0;
    let present = 0;
    for (let j = 0; j < 12; j++) {
      const k = end - 11 + j;
      const rate = rateAt(k);
      if (Number.isNaN(rate)) continue;
      p[(lastMonth - 11 - 12 * b + j) % 12] = rate / trendAt(k);
      sum += rate / trendAt(k);
      present++;
    }
    const mean = sum / present;
    return p.map((r) => r / mean);
  });
  const years = profiles.length;
  const raw = FLAT.map((_, c) => {
    const xs = profiles.map((p) => p[c]).filter(Number.isFinite);
    return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 1;
  });
  const corrs = profiles.slice(1).map((p, i) => pearson(profiles[i], p));
  const known = corrs.filter((c): c is number => c !== null);
  const corr = known.length ? known.reduce((a, b) => a + b, 0) / known.length : null;
  const ok = years >= 2 && corr !== null && corr >= FORECAST.seasonMinCorr;
  const perSlot = units / 12;
  const weight = ok ? perSlot / (perSlot + FORECAST.seasonShrink) : 0;
  const shrunk = raw.map((r) => 1 + weight * (r - 1));
  const mean = shrunk.reduce((a, b) => a + b, 0) / 12;
  return { factors: shrunk.map((f) => f / mean), raw, years, corr, weight, unitsPerYear: years ? units / years : 0, ok };
}

export type SeasonSource = ForecastBase["season_from"];
export type SeasonChoice = { from: SeasonSource; key: string; profile: SeasonProfile | null };

/** The first pattern that holds up, in order: the item's own, its product type's, its project's, the store's. */
export function pickSeason(candidates: SeasonChoice[]): SeasonChoice {
  return candidates.find((c) => c.profile?.ok) ?? { from: "none", key: "", profile: null };
}

/** What the nightly job stores per item (forecast_base without id and updated_at). */
export type BaseRow = Omit<ForecastBase, "id" | "updated_at">;

/**
 * How the base rate is put together. FORECAST holds the values in use; `node server/src/cli.ts forecast-eval`
 * backtests others against them.
 */
export type BaseMethod = {
  /** Share of the base rate from the recent window; the rest is the 12-month rate, brought to now by the trend. */
  recentWeight: number;
  /**
   * The same for an item first sold under two years ago (too new for a trend): a new item's sales usually settle
   * after its launch, which its 12 months still include, so it leans on the recent rate.
   */
  recentWeightYoung: number;
  /**
   * The trend measures a whole year's change, but the 12-month window's middle is only about 6 months back,
   * so the 12-month rate is scaled by trend ^ this: 1 = all of it, 0.5 = half a year's worth (to now), 0 = none.
   */
  trendPower: number;
  /**
   * When the recent rate has to agree before the trend counts: "none" (always), "direction" (only when the recent
   * rate is above the 12-month rate for growth, below it for a decline), "bound" (also never past the recent rate).
   */
  trendConfirm: "none" | "direction" | "bound";
  /**
   * In-stock days a window needs for the whole stockout adjustment. With fewer, its rate moves toward the raw
   * rate (units ÷ all its days) in proportion, since a few days after a restock oversell. 0 = always all of it.
   */
  upliftDays: number;
  /** The recent window reaches back until it has this many in-stock days (up to a year). */
  minInStock: number;
  /**
   * Confidence weighting: with fewer in-stock days than this in the recent window, its weight shrinks in
   * proportion and the 12-month rate's grows. 0 = off.
   */
  confidenceDays: number;
};

/** A window walked back from its last day: units, all days and in-stock days, with their seasonal weights. */
export type Window = {
  days: number;
  units: number;
  inStock: number;
  /** Seasonal factors summed over the in-stock days (= in-stock days without seasonality). */
  weight: number;
  /** Seasonal factors summed over all its days. */
  all: number;
  /** In-stock days with no stock history (counted as in stock). */
  raw: number;
};

/** A window's rate per day at an average season: units ÷ in-stock days, moved toward units ÷ all days on a thin sample. */
export function windowRate(w: Window, upliftDays: number): number {
  const adjusted = w.weight > 0 ? Math.max(0, w.units) / w.weight : 0;
  if (!(upliftDays > 0) || w.inStock >= upliftDays) return adjusted;
  const raw = w.all > 0 ? Math.max(0, w.units) / w.all : 0;
  return raw + (w.inStock / upliftDays) * (adjusted - raw);
}

export type BaseWindows = {
  recent: Window;
  year: Window;
  prior: Window;
  /** Both years sold enough, and the item is at least two years old. */
  trendable: boolean;
  /** First sold within the last two years. */
  young: boolean;
  stopped: boolean;
  born: number;
  weight: (d: number) => number;
};

/** The windows the base rate is made from as of `asOf` (the last whole day), with `factors` the seasonal pattern. */
export function baseWindows(
  s: DailySeries,
  stockFrom: StockFrom,
  asOf: number,
  factors: number[] | null,
  minInStock: number = FORECAST.minInStock,
): BaseWindows {
  const weight = (d: number) => (factors ? factors[monthOfDay(d) % 12] : 1);
  const born = Math.max(s.start, s.firstSold ?? asOf + 1);

  // A window walked back from `end`; stops once it has `minDays` days and `minIn` in-stock days, or at `floor`.
  const walk = (end: number, floor: number, minDays: number, minIn: number): Window => {
    const w = { days: 0, units: 0, inStock: 0, weight: 0, all: 0, raw: 0 };
    for (let d = end; d >= Math.max(born, floor); d--) {
      w.days++;
      w.units += unitsOn(s, d);
      const f = weight(d);
      w.all += f;
      const k = inStock(s, stockFrom, d);
      if (k) {
        w.inStock++;
        w.weight += f;
        if (k === 2) w.raw++;
      }
      if (w.days >= minDays && w.inStock >= minIn) break;
    }
    return w;
  };
  const recent = walk(asOf, asOf - 364, FORECAST.recentDays, minInStock);
  const year = walk(asOf, asOf - 364, 365, Number.POSITIVE_INFINITY);
  const prior = walk(asOf - 365, asOf - 729, 365, Number.POSITIVE_INFINITY);
  const trendable =
    s.firstSold !== null &&
    s.firstSold <= asOf - 729 &&
    year.units >= FORECAST.trendMinUnits &&
    prior.units >= FORECAST.trendMinUnits &&
    year.inStock >= FORECAST.trendMinInStock &&
    prior.inStock >= FORECAST.trendMinInStock;

  // Stopped: out of stock (and no sales) for 180+ days running, all inside the stock history.
  let outRun = 0;
  for (let d = asOf; d >= s.start && inStock(s, stockFrom, d) === 0; d--) outRun++;
  const young = s.firstSold === null || s.firstSold > asOf - 729;
  return { recent, year, prior, trendable, young, stopped: outRun >= FORECAST.stoppedDays, born, weight };
}

/**
 * The base rate from its windows: `recentWeight` of the recent rate plus the rest of the 12-month rate × the
 * trend applied. The trend (12 months ÷ the 12 before, capped) is raised to `trendPower`, and with
 * `trendConfirm` it counts only when the recent rate agrees, so growth that has levelled off isn't projected on.
 */
export function levelOf(w: BaseWindows, m: BaseMethod = FORECAST) {
  const recentRate = windowRate(w.recent, m.upliftDays);
  const yearRate = windowRate(w.year, m.upliftDays);
  const priorRate = windowRate(w.prior, m.upliftDays);
  const trendRaw = w.trendable && priorRate > 0 ? yearRate / priorRate : null;
  const capped = trendRaw === null ? 1 : Math.min(FORECAST.trendMax, Math.max(FORECAST.trendMin, trendRaw));
  let trend = capped ** m.trendPower;
  // "direction": the recent rate has moved off the 12-month rate the same way; "bound": and only as far as it has.
  const agrees = capped > 1 ? recentRate > yearRate : capped < 1 ? recentRate < yearRate : true;
  if (m.trendConfirm !== "none" && !agrees) trend = 1;
  else if (m.trendConfirm === "bound" && yearRate > 0) {
    const toRecent = recentRate / yearRate;
    trend = capped > 1 ? Math.min(trend, toRecent) : capped < 1 ? Math.max(trend, toRecent) : 1;
  }
  const full = w.young ? m.recentWeightYoung : m.recentWeight;
  const recentWeight = m.confidenceDays > 0 && w.recent.inStock < m.confidenceDays ? (full * w.recent.inStock) / m.confidenceDays : full;
  const level = w.stopped ? 0 : recentWeight * recentRate + (1 - recentWeight) * yearRate * trend;
  return { level, recentRate, yearRate, recentWeight, trend, trendRaw };
}

/** The base for one item as of `asOf` (the last whole day), with `factors` the seasonal pattern chosen for it. */
export function computeBase(s: DailySeries, stockFrom: StockFrom, asOf: number, season: SeasonChoice, method: BaseMethod = FORECAST): BaseRow {
  const factors = season.profile?.factors ?? null;
  const windows = baseWindows(s, stockFrom, asOf, factors, method.minInStock);
  const { recent, year, stopped, born, weight } = windows;
  const { level, recentRate, yearRate, trend, trendRaw } = levelOf(windows, method);

  // Day-to-day variability: weeks wholly in stock in the last year, seasonality divided out.
  const weeks: number[] = [];
  for (let end = asOf; end - 6 >= Math.max(born, asOf - 363); end -= 7) {
    let u = 0;
    let w = 0;
    let full = true;
    for (let d = end - 6; d <= end; d++) {
      if (!inStock(s, stockFrom, d)) full = false;
      u += unitsOn(s, d);
      w += weight(d);
    }
    if (full) weeks.push((u / w) * 7);
  }
  let sd = Math.sqrt(level);
  if (weeks.length >= 4) {
    const mean = weeks.reduce((a, b) => a + b, 0) / weeks.length;
    sd = Math.sqrt(weeks.reduce((a, b) => a + (b - mean) ** 2, 0) / (weeks.length - 1)) / Math.sqrt(7);
  }

  // History for the chart: 24 whole months and this one to date.
  const lastMonth = monthOfDay(asOf);
  const fromMonth = lastMonth - FORECAST.historyMonths;
  const unitsByMonth = new Array<number>(FORECAST.historyMonths + 1).fill(0);
  const outByMonth = new Array<number>(FORECAST.historyMonths + 1).fill(-1);
  for (let d = Math.max(s.start, monthFirstDay(fromMonth)); d <= asOf; d++) {
    const k = monthOfDay(d) - fromMonth;
    unitsByMonth[k] += unitsOn(s, d);
    if (known(stockFrom, d)) {
      if (outByMonth[k] < 0) outByMonth[k] = 0;
      if (s.out[d - s.start]) outByMonth[k] += 1;
    }
  }
  const outIn = (n: number) => {
    let out = 0;
    for (let d = asOf - n + 1; d <= asOf; d++) if (known(stockFrom, d) && d >= s.start && s.out[d - s.start]) out++;
    return out;
  };

  const windowDays = recent.days + year.days;
  const rawDays = recent.raw + year.raw;
  const counted = recent.inStock + year.inStock;
  return {
    method: counted && rawDays === counted ? "raw" : rawDays > 0 ? "mixed" : windowDays ? "stockout_adjusted" : "raw",
    level,
    recent_rate: recentRate,
    recent_days: recent.days,
    recent_in_stock: recent.inStock,
    recent_units: recent.units,
    year_rate: yearRate,
    year_days: year.days,
    year_in_stock: year.inStock,
    year_units: year.units,
    raw_days: year.raw,
    trend,
    trend_raw: trendRaw,
    season_from: season.from,
    season_key: season.key,
    seasonal: factors ? factors.slice() : [],
    season_weight: season.profile?.weight ?? 0,
    season_years: season.profile?.years ?? 0,
    sd,
    first_sold: s.firstSold === null ? null : isoOfDay(s.firstSold),
    stopped,
    months_from: monthIso(fromMonth),
    units_by_month: unitsByMonth,
    out_by_month: outByMonth,
    out_30: outIn(30),
    out_90: outIn(90),
    acc_source: "none",
    acc_months: 0,
    acc_actual: 0,
    acc_forecast: 0,
    acc_abs_error: 0,
  };
}

/** Month `m`'s forecast from a base: rate × that month's seasonal factor × its days. */
export const baseMonthUnits = (b: Pick<BaseRow, "level" | "seasonal">, m: number) =>
  Number(b.level) * (b.seasonal?.length === 12 ? Number(b.seasonal[m % 12]) : 1) * monthDays(m);

export type Accuracy = { months: number; actual: number; forecast: number; absError: number };

/**
 * What the method would have said at the start of each of the last `months` whole months (from data up to
 * the day before), against what sold. A month's forecast is scaled to the days it was in stock, so a
 * stockout isn't counted as an error. Months before the item had 30 days of sales, or with nothing
 * forecast and nothing sold, are skipped. `seasonAt` gives the pattern chosen as of each month.
 */
export function backtest(
  s: DailySeries,
  stockFrom: StockFrom,
  asOf: number,
  seasonAt: (month: number) => SeasonChoice,
  months: number = FORECAST.backtestMonths,
): Accuracy {
  const out: Accuracy = { months: 0, actual: 0, forecast: 0, absError: 0 };
  for (const r of backtestMethods(s, stockFrom, asOf, seasonAt, [FORECAST], { months })[0]) {
    out.months++;
    out.actual += r.actual;
    out.forecast += r.forecast;
    out.absError += Math.abs(r.forecast - r.actual);
  }
  return out;
}

/** One backtest month: the forecast (scaled to its in-stock days), what sold, and whether a trend could be measured. */
export type BacktestMonth = { month: number; forecast: number; actual: number; inStock: number; trendable: boolean };

/**
 * backtest() for several methods at once (forecast-eval), month by month; the windows are walked once per month
 * and shared. `ahead`: the forecast is made that many months before the month starts (0 = the day before).
 */
export function backtestMethods(
  s: DailySeries,
  stockFrom: StockFrom,
  asOf: number,
  seasonAt: (month: number) => SeasonChoice,
  methods: readonly BaseMethod[],
  { months = FORECAST.backtestMonths, ahead = 0 }: { months?: number; ahead?: number } = {},
): BacktestMonth[][] {
  const out = methods.map((): BacktestMonth[] => []);
  const lastWhole = monthOfDay(asOf + 1) - 1;
  for (let m = lastWhole - months + 1; m <= lastWhole; m++) {
    const before = monthFirstDay(m - ahead) - 1;
    if (s.firstSold === null || s.firstSold > before - 29) continue;
    const factors = seasonAt(m - ahead).profile?.factors ?? null;
    const stats = monthStats(s, stockFrom, m, m, monthFirstDay(m + 1) - 1);
    const actual = Math.max(0, stats.units[0]);
    // The month's units at the base rate, scaled to the days it was in stock.
    const perUnit = baseMonthUnits({ level: 1, seasonal: factors ?? [] }, m) * (stats.inStock[0] / monthDays(m));
    const windows = new Map<number, BaseWindows>();
    methods.forEach((method, k) => {
      let w = windows.get(method.minInStock);
      if (!w) {
        w = baseWindows(s, stockFrom, before, factors, method.minInStock);
        windows.set(method.minInStock, w);
      }
      const forecast = levelOf(w, method).level * perUnit;
      if (forecast < 0.5 && actual === 0) return;
      out[k].push({ month: m, forecast, actual, inStock: stats.inStock[0], trendable: w.trendable });
    });
  }
  return out;
}

export type Quality = { label: "good" | "fair" | "poor" | "new"; wape: number | null; bias: number | null; text: string };

/** The accuracy chip: weighted error (off by how much a month, as a share of what sold) and bias. */
export function quality(a: Pick<ForecastBase, "acc_months" | "acc_actual" | "acc_forecast" | "acc_abs_error" | "acc_source"> | null | undefined): Quality {
  if (!a || a.acc_months < 3 || Number(a.acc_actual) < 10) return { label: "new", wape: null, bias: null, text: "Not enough history to measure yet" };
  const wape = Number(a.acc_abs_error) / Number(a.acc_actual);
  const bias = (Number(a.acc_forecast) - Number(a.acc_actual)) / Number(a.acc_actual);
  const label = wape <= 0.3 ? "good" : wape <= 0.6 ? "fair" : "poor";
  const src = a.acc_source === "snapshots" ? "saved forecasts" : "backtest";
  return {
    label,
    wape,
    bias,
    text: `Off by ${fmt(Math.round(wape * 100))}% a month over ${a.acc_months} months (${src}); ${Math.abs(bias) < 0.05 ? "no real bias" : `${fmt(Math.round(Math.abs(bias) * 100))}% ${bias > 0 ? "high" : "low"} overall`}`,
  };
}

/** Accuracy summed over many items (a vendor, everything). */
export function sumAccuracy(
  rows: readonly (Pick<ForecastBase, "acc_months" | "acc_actual" | "acc_forecast" | "acc_abs_error" | "acc_source"> | null | undefined)[],
) {
  const t = { acc_months: 0, acc_actual: 0, acc_forecast: 0, acc_abs_error: 0, acc_source: "backtest" as ForecastBase["acc_source"], items: 0 };
  for (const r of rows) {
    if (!r || r.acc_months < 1) continue;
    t.items++;
    t.acc_months = Math.max(t.acc_months, r.acc_months);
    t.acc_actual += Number(r.acc_actual);
    t.acc_forecast += Number(r.acc_forecast);
    t.acc_abs_error += Number(r.acc_abs_error);
    if (r.acc_source === "snapshots") t.acc_source = "snapshots";
  }
  return t;
}

// 2. The forecast people see -------------------------------------------------------------------------------

export type ForecastItem = Pick<Item, "id" | "kind" | "sku" | "name" | "archived" | "project_id" | "tags" | "shopify_product_id">;
export type ForecastBaseRow = Omit<ForecastBase, "updated_at">;
export type AdjustmentRow = Pick<ForecastAdjustment, "id" | "scope" | "scope_value" | "pct" | "month_from" | "month_to" | "note" | "active">;

export type ForecastData = {
  /** YYYY-MM-DD at HHL (planToday()). */
  today: string;
  items: readonly ForecastItem[];
  bases: readonly ForecastBaseRow[];
  /** Sales summaries: the fallback for items the nightly forecast hasn't reached yet. */
  sales: readonly Pick<SalesSummary, "id" | "as_of" | "units_30" | "units_90" | "units_365" | "weekly" | "first_sold" | "last_sold">[];
  overrides: readonly Pick<ForecastOverride, "item_id" | "month" | "units" | "note">[];
  adjustments: readonly AdjustmentRow[];
  itemPlans: readonly Pick<ItemPlan, "id" | "like_item_id" | "like_pct">[];
  products: readonly Pick<ShopifyProduct, "id" | "product_type" | "tags">[];
  vendorItems: readonly Pick<VendorItem, "item_id" | "vendor_id" | "is_preferred" | "created_at">[];
  vendors: readonly Pick<Vendor, "id" | "name" | "short_name">[];
  projects: readonly Pick<Project, "id" | "name" | "code">[];
  /** The latest nightly run (forecast_run): its as-of date and how fresh the stock history is. */
  run?: ForecastRunRow | null;
};

export type ForecastRunRow = Pick<ForecastRun, "as_of" | "sales_through" | "stock_through" | "stale_days">;

/** When the stock history is behind the sales (its pull keeps failing), what that means, in words; null when it's current. */
export function staleText(run: ForecastRunRow | null | undefined): string | null {
  if (!run || run.stale_days <= 2 || !run.stock_through) return null;
  const until = shortDate(String(run.stock_through).slice(0, 10), String(run.as_of).slice(0, 10));
  return run.stale_days <= FORECAST.staleCapDays
    ? `Stock history stops at ${until}, ${run.stale_days} days behind sales, so the forecast only counts days through then`
    : `Stock history stops at ${until}, ${run.stale_days} days behind sales: the days since count as in stock, so stockouts in them look like weak demand`;
}

export type FlagCode = "raw" | "mixed" | "short" | "like" | "stopped" | "long_stockout" | "pending" | "stale";
export type ForecastFlag = { code: FlagCode; message: string };

export type AppliedAdjustment = { id: string; pct: number; label: string; months: string };

export type MonthForecast = {
  month: string;
  days: number;
  /** Units at the base rate × the month's seasonal factor (before adjustments). */
  base: number;
  season: number;
  /** All matching adjustments multiplied. */
  factor: number;
  adjustments: AppliedAdjustment[];
  override: number | null;
  overrideNote: string;
  units: number;
};

export type ItemForecast = {
  itemId: string;
  /** The item's own nightly base (its history), if it has one. */
  base: ForecastBaseRow | null;
  /** Where the numbers come from: the nightly base, the sales summary (not computed yet), another item, or nothing. */
  from: "history" | "summary" | "like" | "none";
  like: { itemId: string; sku: string; pct: number } | null;
  /** Units per day at an average time of year, after "forecast like". */
  level: number;
  /** 12 factors, Jan–Dec. */
  seasonal: number[];
  seasonFrom: SeasonSource;
  seasonKey: string;
  trend: number;
  sd: number;
  stopped: boolean;
  /** Days since the first sale, as of the nightly run (0 = unknown). */
  historyDays: number;
  /** Whether stock history covers the recent window (the stopped-selling rule applies when it doesn't). */
  stockAdjusted: boolean;
  /** The 12 months from the current one. */
  months: MonthForecast[];
  next30: number;
  next90: number;
  next365: number;
  flags: ForecastFlag[];
  /** Adjustments that match this item (any month). */
  adjustments: AppliedAdjustment[];
  /** Forecast units per day in month `m` (overrides and adjustments included). */
  dayRate: (m: number) => number;
  /** explainForecast() for this item (the plan's why uses it). */
  why: () => string[];
};

export type Forecasts = {
  today: string;
  month: number;
  byItem: Map<string, ItemForecast>;
  run: ForecastRunRow | null;
  /** Bumped by every full build; one built from a previous run (`reuse`) keeps it, so a plan built on the
   *  same generation knows only overrides and item settings have changed since. */
  generation: number;
};

let generations = 0;

/** Months ahead with precomputed rates; later ones are worked out when asked. */
const CACHED_MONTHS = 60;

const textOf = (d: string | Date | null | undefined) => (d == null ? null : typeof d === "string" ? d.slice(0, 10) : d.toISOString().slice(0, 10));

/** The scope's name for people: "Project GCU 2", "Vendor Tailhoo", "Product type Shell", "Tag sale", "All items". */
export function scopeLabel(a: Pick<AdjustmentRow, "scope" | "scope_value">, data: Pick<ForecastData, "vendors" | "projects" | "items">): string {
  switch (a.scope) {
    case "all":
      return "All items";
    case "vendor": {
      const v = data.vendors.find((x) => x.id === a.scope_value);
      return `Vendor ${v ? v.short_name || v.name : "(removed)"}`;
    }
    case "project": {
      const p = data.projects.find((x) => x.id === a.scope_value);
      return `Project ${p ? p.name : "(removed)"}`;
    }
    case "product_type":
      return `Product type ${a.scope_value || "(none)"}`;
    case "tag":
      return `Tag ${a.scope_value}`;
    case "item":
      return `${data.items.find((x) => x.id === a.scope_value)?.sku || "Item"}`;
  }
}

/**
 * Every sell item's forecast (make items included; archived ones left out). With `reuse`, only the items in
 * `only` are worked out again and the rest come from `prev` (an override or an item's settings changed), so an
 * edit costs a few milliseconds, not a full rebuild.
 */
export function buildForecasts(data: ForecastData, reuse?: { prev: Forecasts; only: ReadonlySet<string> }): Forecasts {
  const today = data.today;
  const t0 = dayNum(today);
  const m0 = monthNum(today);
  const baseOf = new Map(data.bases.map((b) => [b.id, b]));
  const salesOf = new Map(data.sales.map((s) => [s.id, s]));
  const planOf = new Map(data.itemPlans.map((p) => [p.id, p]));
  const productOf = new Map(data.products.map((p) => [p.id, p]));
  const itemOf = new Map(data.items.map((i) => [i.id, i]));
  const vendorOf = new Map<string, string>();
  for (const l of [...data.vendorItems].sort(
    (a, b) => Number(b.is_preferred) - Number(a.is_preferred) || String(a.created_at).localeCompare(String(b.created_at)),
  )) {
    if (!vendorOf.has(l.item_id)) vendorOf.set(l.item_id, l.vendor_id);
  }
  const overridesOf = new Map<string, Map<number, { units: number; note: string }>>();
  for (const o of data.overrides) {
    const m = overridesOf.get(o.item_id) ?? new Map();
    m.set(monthNum(textOf(o.month) ?? today), { units: Number(o.units), note: o.note ?? "" });
    overridesOf.set(o.item_id, m);
  }
  const adjustments = data.adjustments
    .filter((a) => a.active)
    .map((a) => {
      const from = textOf(a.month_from);
      const to = textOf(a.month_to);
      return {
        row: a,
        from: from ? monthNum(from) : Number.NEGATIVE_INFINITY,
        to: to ? monthNum(to) : Number.POSITIVE_INFINITY,
        applied: { id: a.id, pct: Number(a.pct), label: `${scopeLabel(a, data)} ${pctText(Number(a.pct))}`, months: monthRange(from, to) },
      };
    });
  const matches = (a: AdjustmentRow, item: ForecastItem) => {
    const product = item.shopify_product_id ? productOf.get(item.shopify_product_id) : undefined;
    switch (a.scope) {
      case "all":
        return true;
      case "vendor":
        return vendorOf.get(item.id) === a.scope_value;
      case "project":
        return item.project_id === a.scope_value;
      case "product_type":
        return !!product && product.product_type.toLowerCase() === a.scope_value.toLowerCase();
      case "tag": {
        const tag = a.scope_value.toLowerCase();
        return (item.tags ?? []).some((t) => t.toLowerCase() === tag) || (product?.tags ?? []).some((t) => t.toLowerCase() === tag);
      }
      case "item":
        return item.id === a.scope_value;
    }
  };

  /** What an item's own history says: the nightly base, else its sales summary (flat, not stockout-adjusted). */
  const own = (id: string) => {
    const b = baseOf.get(id);
    if (b) {
      const seasonal = b.seasonal?.length === 12 ? b.seasonal.map(Number) : FLAT;
      return {
        from: "history" as const,
        base: b,
        level: Number(b.level),
        seasonal,
        seasonFrom: b.season_from,
        seasonKey: b.season_key,
        trend: Number(b.trend),
        sd: Number(b.sd),
        stopped: b.stopped,
        stockAdjusted: b.method !== "raw",
      };
    }
    const s = salesOf.get(id);
    if (s) {
      const r = salesRates(s);
      return {
        from: "summary" as const,
        base: null,
        level: r.rate,
        seasonal: FLAT,
        seasonFrom: "none" as SeasonSource,
        seasonKey: "",
        trend: 1,
        sd: r.sd,
        stopped: false,
        stockAdjusted: false,
      };
    }
    return null;
  };

  const run = data.run ?? null;
  const stale = staleText(run);
  const byItem = new Map<string, ItemForecast>();
  for (const item of data.items) {
    if (item.kind !== "sell" || item.archived) continue;
    const kept = reuse && !reuse.only.has(item.id) ? reuse.prev.byItem.get(item.id) : undefined;
    if (kept) {
      byItem.set(item.id, kept);
      continue;
    }
    const flags: ForecastFlag[] = [];
    const flag = (code: FlagCode, message: string) => flags.push({ code, message });
    const mine = own(item.id);
    const ip = planOf.get(item.id);
    const likeItem = ip?.like_item_id ? itemOf.get(ip.like_item_id) : undefined;
    const likeSrc = likeItem && likeItem.id !== item.id ? own(likeItem.id) : null;
    const pct = Number(ip?.like_pct ?? 100);
    const src = likeSrc ?? mine;
    const scale = likeSrc ? pct / 100 : 1;
    const like = likeSrc && likeItem ? { itemId: likeItem.id, sku: likeItem.sku, pct } : null;
    if (like) flag("like", `Forecast like ${like.sku} at ${fmt(pct)}%`);
    else if (ip?.like_item_id) flag("like", 'Its "forecast like" item is gone or has no sales; using its own history');

    const b = mine?.base ?? null;
    const historyDays = b?.first_sold && run ? dayNum(String(run.as_of).slice(0, 10)) - dayNum(String(b.first_sold).slice(0, 10)) + 1 : 0;
    if (!like && mine) {
      if (mine.from === "summary") flag("pending", "The nightly forecast hasn't run for this item yet: raw sales, no seasonality or trend");
      else if (b?.method === "raw") flag("raw", "No stock history for these dates: raw sales, so stockouts look like weak demand");
      else if (b?.method === "mixed") flag("mixed", `${fmt(b.raw_days)} days have no stock history and count as in stock`);
      if (historyDays > 0 && historyDays < 60) flag("short", `Only ${fmt(historyDays)} days of sales: the forecast may move a lot`);
      if (b && stale) flag("stale", stale);
      if (b?.stopped) flag("stopped", `Out of stock ${FORECAST.stoppedDays}+ days in a row: treated as stopped selling (add an override to plan it)`);
      else if (b && b.out_90 >= 60) flag("long_stockout", `Out of stock ${fmt(b.out_90)} of the last 90 days: the rate reaches back to when it was in stock`);
    }

    const level = src ? src.level * scale : 0;
    const seasonal = src?.seasonal ?? FLAT;
    const stopped = !like && !!mine?.stopped;
    const mineAdj = adjustments.filter((a) => matches(a.row, item));
    const overrides = overridesOf.get(item.id);
    const monthRate = (m: number) => {
      const o = overrides?.get(m);
      if (o) return o.units / monthDays(m);
      let f = 1;
      for (const a of mineAdj) if (m >= a.from && m <= a.to) f *= 1 + a.applied.pct / 100;
      return (stopped ? 0 : level) * seasonal[m % 12] * f;
    };
    const rates = new Float64Array(CACHED_MONTHS);
    for (let k = 0; k < CACHED_MONTHS; k++) rates[k] = monthRate(m0 + k);
    const dayRate = (m: number) => (m >= m0 && m < m0 + CACHED_MONTHS ? rates[m - m0] : monthRate(m));

    const months: MonthForecast[] = [];
    for (let k = 0; k < 12; k++) {
      const m = m0 + k;
      const days = monthDays(m);
      const o = overrides?.get(m);
      const applied = mineAdj.filter((a) => m >= a.from && m <= a.to).map((a) => a.applied);
      const factor = applied.reduce((n, a) => n * (1 + a.pct / 100), 1);
      const base = (stopped ? 0 : level) * seasonal[m % 12] * days;
      months.push({
        month: monthIso(m),
        days,
        base,
        season: seasonal[m % 12],
        factor,
        adjustments: applied,
        override: o ? o.units : null,
        overrideNote: o?.note ?? "",
        units: o ? o.units : base * factor,
      });
    }
    const ahead = (n: number) => {
      let total = 0;
      let d = t0;
      const end = t0 + n;
      while (d < end) {
        const m = monthOfDay(d);
        const next = Math.min(end, monthFirstDay(m + 1));
        total += (next - d) * dayRate(m);
        d = next;
      }
      return total;
    };
    const forecast: ItemForecast = {
      itemId: item.id,
      base: b,
      from: like ? "like" : (mine?.from ?? "none"),
      like,
      level,
      seasonal,
      seasonFrom: src?.seasonFrom ?? "none",
      seasonKey: src?.seasonKey ?? "",
      trend: src?.trend ?? 1,
      sd: (src?.sd ?? 0) * scale,
      stopped,
      historyDays,
      stockAdjusted: like ? !!likeSrc?.stockAdjusted : !!mine?.stockAdjusted,
      months,
      next30: ahead(30),
      next90: ahead(90),
      next365: ahead(365),
      flags,
      adjustments: mineAdj.map((a) => a.applied),
      dayRate,
      why: () => explainForecast(forecast),
    };
    byItem.set(item.id, forecast);
  }
  return { today, month: m0, byItem, run, generation: reuse ? reuse.prev.generation : ++generations };
}

// The why -------------------------------------------------------------------------------------------------

const SEASON_FROM_TEXT: Record<SeasonSource, string> = {
  item: "the item's own sales",
  product_type: "its product type",
  project: "its project",
  store: "the whole store",
  none: "",
};
export const SEASON_LABELS: Record<SeasonSource, string> = {
  item: "Own",
  product_type: "Type",
  project: "Project",
  store: "Store",
  none: "Flat",
};

/** The share of the stockout adjustment a window with `inStock` of `days` in stock gets (1 = all of it). */
export const upliftShare = (inStock: number, days: number) =>
  FORECAST.upliftDays > 0 && inStock < FORECAST.upliftDays && inStock < days ? inStock / FORECAST.upliftDays : 1;

/**
 * The recent window's weight in a base row, and whether it's the young-item one, worked out from the stored rates
 * (the row doesn't keep it): whichever of FORECAST's weights gives its level.
 */
export function recentWeightOf(b: Pick<ForecastBaseRow, "level" | "recent_rate" | "year_rate" | "trend" | "recent_in_stock">) {
  const conf = FORECAST.confidenceDays > 0 && b.recent_in_stock < FORECAST.confidenceDays ? b.recent_in_stock / FORECAST.confidenceDays : 1;
  const recent = Number(b.recent_rate);
  const year = Number(b.year_rate) * Number(b.trend);
  const off = (w: number) => Math.abs(w * recent + (1 - w) * year - Number(b.level));
  const usual = FORECAST.recentWeight * conf;
  const young = FORECAST.recentWeightYoung * conf;
  const isYoung = young !== usual && Math.abs(recent - year) > 1e-3 * Math.max(recent, year) && off(young) < off(usual);
  return { weight: isYoung ? young : usual, young: isYoung, thin: conf < 1 };
}

/** The base rate, seasonality and trend in words (one sentence each). */
export function explainBase(b: ForecastBaseRow): string[] {
  const out: string[] = [];
  if (b.stopped) return [`Out of stock ${FORECAST.stoppedDays}+ days in a row with no sales, so it's treated as stopped selling: forecast 0.`];
  const seasonal = b.seasonal?.length === 12;
  const adj = seasonal ? ", time of year taken out" : "";
  const pct = (x: number) => `${fmt(Math.round(x * 100))}%`;
  const window = (units: number, inStock: number, days: number, rate: number, label: string) => {
    if (!days) return `${label}: no sales yet.`;
    const out = days - inStock;
    const share = upliftShare(inStock, days);
    const thin =
      share < 1
        ? `, with ${pct(share)} of the stockout adjustment: ${fmt(inStock)} in-stock day${inStock === 1 ? " is" : "s are"} too few to trust it all (${FORECAST.upliftDays} needed), since sales right after a restock run high`
        : "";
    return `${label}: ${fmt(units)} sold in ${fmt(inStock)} in-stock day${inStock === 1 ? "" : "s"}${out > 0 ? ` (${fmt(out)} out of stock)` : ""} = ${fmt(rate)}/day${adj}${thin}.`;
  };
  const recentLabel =
    b.recent_days > FORECAST.recentDays
      ? `Last ${fmt(b.recent_days)} days (reaching back for ${FORECAST.minInStock} in-stock days)`
      : `Last ${fmt(b.recent_days)} days`;
  out.push(window(b.recent_units, b.recent_in_stock, b.recent_days, Number(b.recent_rate), recentLabel));
  if (b.year_days > b.recent_days)
    out.push(
      window(
        b.year_units,
        b.year_in_stock,
        b.year_days,
        Number(b.year_rate),
        b.year_days >= 365 ? "Last 12 months" : `Since the first sale (${fmt(b.year_days)} days)`,
      ),
    );
  const trend = Number(b.trend);
  const level = `Base rate ${fmt(Number(b.level))}/day`;
  if (b.year_days > b.recent_days) {
    const { weight, young, thin } = recentWeightOf(b);
    const why = [
      young ? "it first sold under two years ago, and a new item's sales usually settle after its launch, which its 12 months still include" : "",
      thin ? `the recent window has only ${fmt(b.recent_in_stock)} in-stock days, so it counts for less` : "",
    ].filter(Boolean);
    out.push(
      weight >= 1
        ? `${level}: the recent rate alone (${why.join("; ")}).`
        : `${level}: ${pct(weight)} the recent rate, ${pct(1 - weight)} the 12-month rate${trend !== 1 ? ` × the trend (${factorText(trend)})` : ""}${why.length ? ` (${why.join("; ")})` : ""}.`,
    );
  } else out.push(`${level}.`);
  if (b.method === "raw") out.push("No stock history for these days (Shopify's starts in August 2023), so days out of stock count as selling days.");
  else if (b.raw_days > 0) out.push(`${fmt(b.raw_days)} of those days predate Shopify's stock history and count as in stock.`);
  if (b.trend_raw !== null) {
    const raw = Number(b.trend_raw);
    const capped = Math.min(FORECAST.trendMax, Math.max(FORECAST.trendMin, raw));
    const measured = `the last 12 months sold ${raw.toFixed(2)}× the 12 before (in-stock days, time of year taken out)${raw !== capped ? `, capped at ${FORECAST.trendMin}×–${FORECAST.trendMax}×` : ""}`;
    const share = capped ** FORECAST.trendPower;
    const part = FORECAST.trendPower === 0.5 ? "half a year's worth" : `${FORECAST.trendPower} of a year's worth`;
    if (capped === 1 || trend === 1)
      out.push(
        capped === 1
          ? `No trend: ${measured}.`
          : `Trend not used: ${measured}, but the recent rate is ${capped > 1 ? "below" : "above"} the 12-month rate, so the ${capped > 1 ? "growth" : "decline"} has levelled off.`,
      );
    else if (Math.abs(trend - share) < 0.002)
      out.push(`Trend ${factorText(trend)}: ${measured}; the 12-month rate is centred about six months back, so it gets ${part} (to now).`);
    else
      out.push(
        `Trend ${factorText(trend)}: ${measured}; ${part} would be ${factorText(share)}, but it only brings the 12-month rate as far as the recent rate, no further.`,
      );
  }
  if (b.season_from === "none") out.push("No seasonality: not enough history anywhere to trust a pattern.");
  else {
    const peak = b.seasonal.map(Number).reduce((best, f, i, all) => (f > all[best] ? i : best), 0);
    const low = b.seasonal.map(Number).reduce((best, f, i, all) => (f < all[best] ? i : best), 0);
    out.push(
      `Seasonality from ${SEASON_FROM_TEXT[b.season_from]}${b.season_key ? ` (${b.season_key})` : ""}, ${b.season_years} years${b.season_from !== "item" ? " (its own history is too short or too noisy)" : ""}: busiest ${MONTH_SHORT[peak]} (${Number(b.seasonal[peak]).toFixed(2)}×), quietest ${MONTH_SHORT[low]} (${Number(b.seasonal[low]).toFixed(2)}×); ${fmt(Math.round(Number(b.season_weight) * 100))}% of the pattern kept, the rest pulled toward 1 for noise.`,
    );
  }
  return out;
}

/** Everything behind an item's forecast, one plain sentence each: for the item panel, the plan's why and the MCP. */
export function explainForecast(f: ItemForecast): string[] {
  const out: string[] = [];
  if (f.like) {
    out.push(`Forecast like ${f.like.sku} at ${fmt(f.like.pct)}%: ${fmt(f.level)}/day at an average time of year.`);
  } else if (f.from === "history" && f.base) out.push(...explainBase(f.base));
  else if (f.from === "summary")
    out.push(`${fmt(f.level)}/day from raw sales (half the last 90 days, half the last 12 months); the nightly forecast hasn't run yet.`);
  else out.push("No sales to forecast from. Add monthly overrides, or forecast it like another item.");
  for (const a of f.adjustments) out.push(`Adjustment: ${a.label} (${a.months}).`);
  const overridden = f.months.filter((m) => m.override !== null);
  if (overridden.length)
    out.push(`Overridden: ${overridden.map((m) => `${monthLabel(m.month)} (${fmt(m.units)}${m.overrideNote ? `, ${m.overrideNote}` : ""})`).join(", ")}.`);
  out.push(`Next 30 days ${fmt(Math.round(f.next30))}, 90 days ${fmt(Math.round(f.next90))}, 12 months ${fmt(Math.round(f.next365))}.`);
  return out;
}

/** One month in words: "Dec 2026: 9.4/day × 31 days × 1.35 (season) × 1.2 (Project GCU 2 +20%) = 471". */
export function explainMonth(f: ItemForecast, m: MonthForecast): string {
  if (m.override !== null)
    return `${monthLabel(m.month)}: ${fmt(m.override)} (override${m.overrideNote ? `: ${m.overrideNote}` : ""}; the forecast said ${fmt(Math.round(m.base * m.factor))})`;
  const parts = [`${fmt(f.stopped ? 0 : f.level)}/day`, `${m.days} days`];
  if (m.season !== 1) parts.push(`${m.season.toFixed(2)} (season)`);
  for (const a of m.adjustments) parts.push(`${(1 + a.pct / 100).toFixed(2)} (${a.label})`);
  return `${monthLabel(m.month)}: ${parts.join(" × ")} = ${fmt(Math.round(m.units))}`;
}

// Inventory health ---------------------------------------------------------------------------------------------

export type HealthItem = Pick<Item, "id" | "kind" | "sku" | "name" | "archived" | "std_cost" | "price" | "photo_url">;
export type HealthRow = {
  item: HealthItem;
  /** For sale: available at HHL (on hand less open orders, shared/stock.ts) less what's held (Pre-sales). */
  available: number;
  /** Pre-sales --- available but held, not for sale (holds, pre-orders waiting to ship). */
  held: number;
  unitCost: number | null;
  value: number;
  /** Days the stock lasts at the forecast (next 12 months' average); null = no forecast sales. */
  coverDays: number | null;
  overstockUnits: number;
  overstockValue: number;
  /** Stock and no sales in 90 / 180 days. */
  dead90: boolean;
  dead180: boolean;
  lastSold: string | null;
  out30: number;
  out90: number;
  /** Sales lost to stockouts: days out × the base rate (units), × the price ($). */
  lost30: number;
  lost90: number;
  lostRevenue30: number;
  lostRevenue90: number;
  /** Sold ÷ (sold + available), last 90 days. */
  sellThrough90: number | null;
  sold90: number;
};

export type HealthData = {
  today: string;
  items: readonly HealthItem[];
  forecasts: Forecasts;
  /** Available at HHL per item: units on open orders are sold already, so they're no cover, overstock or dead stock. */
  available: Map<string, number>;
  /** Cost per item when std_cost is missing (the preferred vendor's price). */
  vendorCost: Map<string, number>;
  sales: ForecastData["sales"];
  overstockMonths: number;
  /** Pre-sales --- stock at HHL that isn't for sale (holds, pre-orders waiting to ship): left out (shared/presales.ts heldAtHhl()). */
  held?: Map<string, number>;
};

/** Available per item at HHL's own Shopify locations in use (on hand less open orders), like the plan counts it. */
export function stockAtHhl(
  stock: readonly { item_id: string; location_id: string; available: number }[],
  locations: readonly { id: string; kind: string; active: boolean; ignored: boolean }[],
): Map<string, number> {
  const hhl = new Set(locations.filter((l) => l.kind === "hhl" && l.active && !l.ignored).map((l) => l.id));
  const out = new Map<string, number>();
  for (const s of stock) if (hhl.has(s.location_id)) out.set(s.item_id, (out.get(s.item_id) ?? 0) + Number(s.available));
  return out;
}

/** Each item's cost from its preferred (else first) vendor link priced in USD, for items without a standard cost. */
export function vendorCosts(links: readonly Pick<VendorItem, "item_id" | "unit_price" | "currency" | "is_preferred" | "created_at">[]): Map<string, number> {
  const out = new Map<string, number>();
  const sorted = [...links].sort((a, b) => Number(b.is_preferred) - Number(a.is_preferred) || String(a.created_at).localeCompare(String(b.created_at)));
  for (const l of sorted) if (!out.has(l.item_id) && l.unit_price != null && (l.currency || "USD") === "USD") out.set(l.item_id, Number(l.unit_price));
  return out;
}

export function inventoryHealth(d: HealthData) {
  const today = dayNum(d.today);
  const salesOf = new Map(d.sales.map((s) => [s.id, s]));
  const rows: HealthRow[] = [];
  for (const item of d.items) {
    const f = d.forecasts.byItem.get(item.id);
    if (!f) continue;
    // Pre-sales --- held units are HHL's but not for sale: no cover, overstock or dead stock from them.
    const held = Math.min(Math.max(0, d.available.get(item.id) ?? 0), d.held?.get(item.id) ?? 0);
    const available = Math.max(0, (d.available.get(item.id) ?? 0) - held);
    const unitCost = item.std_cost != null ? Number(item.std_cost) : (d.vendorCost.get(item.id) ?? null);
    const perDay = f.next365 / 365;
    const coverDays = perDay > 0 ? available / perDay : null;
    const keep = d.overstockMonths * 30.44 * perDay;
    const overstockUnits = perDay > 0 ? Math.max(0, available - keep) : available;
    const s = salesOf.get(item.id);
    const lastSold = textOf(s?.last_sold) ?? null;
    const sinceSale = lastSold ? today - dayNum(lastSold) : Number.POSITIVE_INFINITY;
    const b = f.base;
    const rate = b ? Number(b.level) : 0;
    const price = Number(item.price ?? 0);
    const out30 = b?.out_30 ?? 0;
    const out90 = b?.out_90 ?? 0;
    const sold90 = Math.max(0, s?.units_90 ?? 0);
    rows.push({
      item,
      available,
      held,
      unitCost,
      value: unitCost != null ? available * unitCost : 0,
      coverDays,
      overstockUnits,
      overstockValue: unitCost != null ? overstockUnits * unitCost : 0,
      dead90: available > 0 && sinceSale > 90,
      dead180: available > 0 && sinceSale > 180,
      lastSold,
      out30,
      out90,
      lost30: out30 * rate,
      lost90: out90 * rate,
      lostRevenue30: out30 * rate * price,
      lostRevenue90: out90 * rate * price,
      sellThrough90: sold90 + available > 0 ? sold90 / (sold90 + available) : null,
      sold90,
    });
  }
  const sum = (pick: (r: HealthRow) => number, only: (r: HealthRow) => boolean = () => true) => rows.reduce((n, r) => (only(r) ? n + pick(r) : n), 0);
  const sold = sum((r) => r.sold90);
  const stock = sum((r) => r.available);
  return {
    rows,
    totals: {
      value: sum((r) => r.value),
      units: stock,
      unpriced: rows.filter((r) => r.available > 0 && r.unitCost == null).length,
      overstockValue: sum((r) => r.overstockValue),
      overstockItems: rows.filter((r) => r.overstockUnits > 0 && r.available > 0).length,
      dead90Value: sum(
        (r) => r.value,
        (r) => r.dead90,
      ),
      dead90Items: rows.filter((r) => r.dead90).length,
      dead180Value: sum(
        (r) => r.value,
        (r) => r.dead180,
      ),
      dead180Items: rows.filter((r) => r.dead180).length,
      stockouts30: rows.filter((r) => r.out30 > 0).length,
      stockouts90: rows.filter((r) => r.out90 > 0).length,
      lostRevenue30: sum((r) => r.lostRevenue30),
      lostRevenue90: sum((r) => r.lostRevenue90),
      sellThrough90: sold + stock > 0 ? sold / (sold + stock) : null,
    },
  };
}
